import React from 'react';
import { Text, i18n, tokens, localizationMaps } from '@simplybenefits/sb-ui-library';
import * as dates from '@simplybenefits/sb-dates';
import globalStyles from '../../theme/globalStyles.js';
import { numberWithCommas } from '../../services/helpers.js';
import { styled } from '@mui/system';

/**
 * 
 * @param {*} status 
 * @returns 
 */
const getMemberFacingStatusAndColor = (status) => {
    // These ones spark joy
    if ([ 'approved', 'paid' ].includes(status)) {
        return {
            statusText: localizationMaps.fromEnglish("claimStatuses", status),
            statusColor: '#6DC067'
        };
    }

    // These ones do not spark joy
    if ([ 'declined', 'reversed' ].includes(status)) {
        return {
            statusText: i18n.t(tokens.labels.claims.declined),
            statusColor: '#D93025'
        };
    }

    // for all other statuses show pending
    return {
        statusText: i18n.t(tokens.labels.claims.pending),
        statusColor: '#FFA500'
    };
};

// have to use styled api 
const BlockRow = styled('div')(({ theme }) => ({
    ...globalStyles.blockRow,
    marginBottom: 0,
    padding: "0 0 0 10px",
    '&:hover': {
        borderLeft: `${ theme.palette.primary.main } solid 5px`,
        padding: '0 0 0 5px',
        backgroundColor: '#F7F9FA'
    }
}));

const HeaderRow = styled('div')({
    ...globalStyles.blockRow,
    marginBottom: 0,
    padding: "0 0 0 10px",
    '&:hover': {
        cursor: 'default'
    }
});

/**
 *
 * @param {*} param0
 */
const ClaimBlock = ({ user, claimID, uid, submitted = 0, claimed = 0, type, status, date, isHeader, isSpendingAccount, amount, testid }) => {
    const { statusColor, statusText } = getMemberFacingStatusAndColor(status);
    const serviceType = localizationMaps.fromEnglish("benefits", type);

    const styles = {
        typeBlock: {
            display: 'flex',
            flex: 2,
            alignItems: 'flex-start',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            padding: "0 10px"
        },
        spendAccountType: {
            display: 'flex',
            flex: 2,
            alignItems: 'flex-start',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            padding: "0 10px"
        },
        spendAccountBody: {
            display: 'flex',
            flex: 2,
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            padding: "0 20px"
        },
        spendAccountAmount: {
            display: 'flex',
            flex: 0.5,
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            padding: "0 20px"
        },
        block: {
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            padding: "0 20px"
        },
        date: {
            display: 'flex',
            flex: 2,
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            padding: "0 20px"
        }
    };

    if (isHeader) {
        return (
            <HeaderRow>
                <div style={styles.typeBlock}>
                    <Text small bold>{ i18n.t(tokens.labels.claims.recentClaims) }</Text>
                </div>

                <div style={styles.date}>
                    <Text small>{ i18n.t(tokens.labels.claims.serviceDate) }</Text>
                </div>

                <div style={ { ...styles.block, justifyContent: isSpendingAccount ? 'center' : 'flex-start' } }>
                    <Text small>{ i18n.t(tokens.labels.claims.identifier) }</Text>
                </div>

                {
                    !isSpendingAccount ?
                        <div style={styles.block}>
                            <Text small>{ i18n.t(tokens.labels.claims.submitted) }</Text>
                        </div>
                        :
                        null
                }

                <div style={styles.block}>
                    <Text small>{ i18n.t(tokens.labels.claims.claimed) }</Text>
                </div>

                <div style={ { ...styles.block, justifyContent: 'flex-start' } } >
                    <Text small>{ i18n.t(tokens.labels.claims.status) }</Text>
                </div>
            </HeaderRow>
        );
    }

    if (isSpendingAccount) {
        return (
            <BlockRow sx={{ borderTop: "solid .5px #D6D6D6", marginBottom: 0 }}>
                <div style={ styles.spendAccountType }>
                    <Text medium light>{ user }</Text>
                    <Text tiny light>
                        { serviceType?.charAt(0)?.toUpperCase() + serviceType?.slice(1) }
                    </Text>
                </div>

                <div style={ styles.date }>
                    <Text
                        small
                        light
                    >
                        { dates.formatDateForDisplay(date, 'll', i18n.locale) }
                    </Text>
                </div>

                <div style={ { ...styles.block, justifyContent: 'center' } } >
                    <Text
                        small
                        light
                    >
                        { uid }
                    </Text>
                </div>

                <div style={ styles.block }>
                    <Text
                        medium
                        bold
                    >
                        { `$${ numberWithCommas(amount) }` }
                    </Text>
                </div>

                <div style={ { ...styles.block, justifyContent: 'flex-start' } } >
                    <Text medium bold margin="0 8px 0 0" color={ statusColor }>•</Text>
                    <Text medium light>
                        { statusText.charAt(0).toUpperCase() + statusText.slice(1) }
                    </Text>
                </div>
            </BlockRow>
        );
    }

    return (
        // eslint-disable-next-line no-return-assign
        <BlockRow
            id={ testid ? testid : 'none' }
            style={ { borderTop: "solid .5px #D6D6D6", marginBottom: 0 } }
            onClick={ () => {
                window.location.href = `/claims/${ claimID }`;
            }}
        >
            <div style={styles.typeBlock}>
                <Text medium bold>{ user }</Text>
                <Text tiny light>
                    { serviceType.charAt(0).toUpperCase() + serviceType.slice(1) }
                </Text>
            </div>

            <div style={styles.date}>
                <Text medium light>
                    { dates.formatDateForDisplay(date, 'll', i18n.locale) }
                </Text>
            </div>

            <div style={ { ...styles.block, justifyContent: 'flex-start' } }>
                <Text medium light>
                    { uid }
                </Text>
            </div>

            <div style={styles.block}>
                <Text medium light>
                    { `$${ numberWithCommas(submitted) }` }
                </Text>
            </div>

            <div style={styles.block}>
                <Text medium bold>
                    { `$${ numberWithCommas(claimed) }` }
                </Text>
            </div>

            <div style={ { ...styles.block, justifyContent: 'flex-start' } }>
                <Text medium bold margin="0 8px 0 0" color={ statusColor }>•</Text>
                <Text medium light>
                    { statusText.charAt(0).toUpperCase() + statusText.slice(1) }
                </Text>
            </div>
        </BlockRow>
    );
};

export default ClaimBlock;